import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Slider,
  Tabs,
  Tab,
  Paper,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import { styled } from '@mui/system';
import BrightnessHighIcon from '@mui/icons-material/BrightnessHigh';
import ContrastIcon from '@mui/icons-material/Contrast';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ExposureIcon from '@mui/icons-material/Exposure';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';

const OptionsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  width: '100%',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  animation: 'fadeIn 0.3s ease-out',
  maxHeight: '90vh',
  overflowY: 'auto',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const PreviewBox = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  minHeight: 150,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: '40px',
  padding: '6px 12px',
  minWidth: '0',
  borderRadius: '20px',
  margin: '0 4px',
  transition: 'all 0.2s ease',
  '&.Mui-selected': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(138, 43, 226, 0.2)' : 'rgba(138, 43, 226, 0.1)',
    fontWeight: 600
  },
  [theme.breakpoints.up('sm')]: {
    minWidth: '80px',
  },
}));

const OptionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    fontSize: '1.2rem'
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  padding: 4,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
  },
  zIndex: 10 // Ensure button is above other elements
}));

const SliderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)',
  borderRadius: theme.spacing(1.5),
  marginTop: theme.spacing(1)
}));

const AdjustToolOptions = ({ settings, onChange, onApply, currentImage }) => {
  const [activeTab, setActiveTab] = useState('brightness');
  const [previewStyle, setPreviewStyle] = useState({});
  
  // Update preview when settings change
  useEffect(() => {
    updatePreview();
  }, [settings, currentImage]);
  
  const updatePreview = () => {
    const { brightness, contrast, saturation, exposure } = settings;
    
    const newStyle = {
      filter: `
        brightness(${1 + brightness / 100})
        contrast(${1 + contrast / 100})
        saturate(${1 + saturation / 100})
        ${exposure !== 0 ? `brightness(${1 + exposure / 200})` : ''}
      `,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      maxHeight: '150px',
      transition: 'filter 0.3s ease'
    };
    
    setPreviewStyle(newStyle);
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleSliderChange = (event, newValue) => {
    onChange({ ...settings, [activeTab]: newValue });
  };
  
  const getSliderValue = () => {
    return settings[activeTab] || 0;
  };
  
  const handleReset = () => {
    onChange({ 
      brightness: 0, 
      contrast: 0, 
      saturation: 0, 
      exposure: 0 
    });
  };
  
  const handleResetSingle = () => {
    onChange({ 
      ...settings,
      [activeTab]: 0
    });
  };
  
  const getTabIcon = (tab) => {
    switch(tab) {
      case 'brightness': return <BrightnessHighIcon fontSize="small" />;
      case 'contrast': return <ContrastIcon fontSize="small" />;
      case 'saturation': return <ColorLensIcon fontSize="small" />;
      case 'exposure': return <ExposureIcon fontSize="small" />;
      default: return null;
    }
  };
  
  return (
    <OptionsPaper elevation={4}>
      <Box sx={{ position: 'relative' }}>
        <CloseButton onClick={() => onApply(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>
        
        <Stack spacing={2.5}>
          <Box>
            <OptionTitle>
              <TuneIcon /> Görsel Ayarları
            </OptionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Görselinizin parlaklık, kontrast ve diğer özelliklerini ayarlayın
            </Typography>
          </Box>
          
          <PreviewBox>
            {currentImage && (
              <img 
                src={currentImage} 
                alt="Preview" 
                style={previewStyle}
              />
            )}
            {!currentImage && (
              <Typography variant="body2" color="text.secondary">
                Önizleme yüklenemiyor
              </Typography>
            )}
          </PreviewBox>
          
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              minHeight: '40px',
              '& .MuiTabs-indicator': {
                height: 0,
              },
              '& .MuiTabs-scrollButtons': {
                color: 'primary.main'
              }
            }}
          >
            <StyledTab 
              icon={<BrightnessHighIcon />} 
              label="Parlaklık" 
              value="brightness"
              iconPosition="start"
            />
            <StyledTab 
              icon={<ContrastIcon />} 
              label="Kontrast" 
              value="contrast"
              iconPosition="start"
            />
            <StyledTab 
              icon={<ColorLensIcon />} 
              label="Doygunluk" 
              value="saturation"
              iconPosition="start"
            />
            <StyledTab 
              icon={<ExposureIcon />} 
              label="Pozlama" 
              value="exposure"
              iconPosition="start"
            />
          </Tabs>
          
          <SliderContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {getTabIcon(activeTab)}
                <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                  {activeTab === 'brightness' && 'Parlaklık'}
                  {activeTab === 'contrast' && 'Kontrast'}
                  {activeTab === 'saturation' && 'Doygunluk'}
                  {activeTab === 'exposure' && 'Pozlama'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 500,
                  color: getSliderValue() !== 0 ? 'primary.main' : 'text.secondary'
                }}>
                  {getSliderValue() > 0 ? '+' : ''}{getSliderValue()}
                </Typography>
                <Tooltip title="Sıfırla">
                  <IconButton 
                    size="small" 
                    onClick={handleResetSingle} 
                    sx={{ ml: 0.5 }}
                    disabled={getSliderValue() === 0}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Slider
              value={getSliderValue()}
              onChange={handleSliderChange}
              min={-100}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              marks={[
                { value: -100, label: '-100' },
                { value: 0, label: '0' },
                { value: 100, label: '+100' },
              ]}
              sx={{
                '& .MuiSlider-thumb': {
                  boxShadow: '0 0 0 8px rgba(25, 118, 210, 0.1)'
                }
              }}
            />
          </SliderContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, gap: 2 }}>
            <Button 
              variant="outlined"
              onClick={handleReset}
              startIcon={<RestartAltIcon />}
              fullWidth
            >
              Sıfırla
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => onApply(true)}
              startIcon={<AutoFixHighIcon />}
              fullWidth
            >
              Uygula
            </Button>
          </Box>
        </Stack>
      </Box>
    </OptionsPaper>
  );
};

export default AdjustToolOptions;
